import * as React from "react";

import { BindingPath } from "../../../../../Engine/Core/CoreTypes";
import { nullElement } from "../../../../TypingUtils";
import { ErrorSeverity } from "../ErrorSeverity";
import { ErrorItemDescription } from "../ErrorItemDescription/ErrorItemDescription";
import { TestPageObjectHandle } from "../../../../Services/TestPageObjects/TestPageObjectHandle";
import { useMetrics } from "../../../../Internals/Metrics/MetricsProviderContext";
import { ERROR_LOG_JUSTIFICATION_CLICK, ERROR_LOG_NAVIGATE_CLICK } from "../../../../metricsAction";
import { ErrorLogItem, ItemErrorMessageType } from "../../../../../Engine/Core/ErrorManager/Utils/Models";
import { hasHiddenTextInDescription } from "../ErrorItemDescription/Utils/Utils";
import { safeHTML } from "../../../../../Engine/Core/ErrorManager/Utils/Utils";

import {
    IconWrapper,
    Justification,
    JustificationContainer,
    ListItem,
    ListItemSource,
    ListItemWrapper,
    SourceItemLink,
} from "./ErrorsItem.styles";
import { ErrorItemIcon } from "./ErrorItemIcon";
import { Minitour } from "./Minitour/Minitour";

export interface ErrorsItemProps {
    error: ErrorLogItem;
    type: ErrorSeverity;
    showJustification?: boolean;
    onActivateItem: (paths: BindingPath[], highlanderPath?: string | null) => Promise<void>;
}

export function ErrorsItem(props: ErrorsItemProps): JSX.Element {
    const { type, error } = props;

    const track = useMetrics();

    const handleNavigate = async (bindingPaths: BindingPath[], highlanderPath?: string | null) => {
        track(ERROR_LOG_NAVIGATE_CLICK, { path: bindingPaths[0], fromTooltip: false, place: "source" });
        await props.onActivateItem(bindingPaths, highlanderPath);
    };

    const renderSource = (): JSX.Element | null => {
        const { description, bindingPaths, source, highlanderPath } = props.error;
        const sourceText = source[source.length - 1] ?? "";
        const isContainNavLinkInDescription = description.find(desc => desc.type === ItemErrorMessageType.navLink);

        if (isContainNavLinkInDescription || hasHiddenTextInDescription(props.error.description)) {
            return nullElement;
        }

        return (
            <ListItemSource>
                {bindingPaths ? (
                    <SourceItemLink
                        data-tid="SourceItemText"
                        onClick={() => handleNavigate(bindingPaths, highlanderPath)}>
                        {sourceText}
                    </SourceItemLink>
                ) : (
                    <SourceItemLink disabled data-tid="SourceItemText">
                        {sourceText}
                    </SourceItemLink>
                )}
            </ListItemSource>
        );
    };

    return (
        <TestPageObjectHandle
            id={`ErrorsItem/${error.id}`}
            pageObject={{
                getType: () => "ErrorsItem",
                navigate: async () => {
                    if (error.bindingPaths != undefined) {
                        await handleNavigate(error.bindingPaths);
                    }
                    return undefined;
                },
            }}>
            <Minitour tags={error.tags}>
                <ListItemWrapper>
                    <ListItem touched={error.touched}>
                        <IconWrapper>
                            {/*// @ts-ignore*/}
                            <ErrorItemIcon status={type} isTouched={error.touched} />
                        </IconWrapper>
                        <div data-tid="Description">
                            <ErrorItemDescription
                                error={props.error}
                                onActivateItem={props.onActivateItem}
                                inTooltip={false}
                                doNotRenderNavigationLinks={props.type === ErrorSeverity.Success}
                            />
                            {props.type !== ErrorSeverity.Success && renderSource()}
                            {props.showJustification && props.error.justification && (
                                <Justification
                                    onClick={e => {
                                        e.stopPropagation();
                                        track(ERROR_LOG_JUSTIFICATION_CLICK);
                                    }}>
                                    Основание проверки:{" "}
                                    <JustificationContainer
                                        dangerouslySetInnerHTML={{ __html: safeHTML(props.error.justification) }}
                                    />
                                </Justification>
                            )}
                        </div>
                    </ListItem>
                </ListItemWrapper>
            </Minitour>
        </TestPageObjectHandle>
    );
}
